import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { AppShell, useShell } from '../components/AppShell';
import { SmsBundlesPanel } from '../components/billing/SmsBundlesPanel';
import { Check } from 'lucide-react';
import { usePricing, annualPrice } from '../lib/pricing';
import { supabase } from '../lib/supabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()];
  return `${d.getDate()} ${m}`;
};

const StatusPill = ({ status, cancel_at_period_end }) => {
  if (cancel_at_period_end) return <span className="pill amber">Cancelling at period end</span>;
  if (!status || status === 'cancelled') return <span className="pill gray">No active plan</span>;
  const map = {
    active:   { cls: 'green', label: 'Active' },
    trialing: { cls: 'prem',  label: 'Trial' },
    past_due: { cls: 'amber', label: 'Past due — update card' },
    incomplete: { cls: 'amber', label: 'Incomplete' },
    incomplete_expired: { cls: 'gray', label: 'Expired' },
    unpaid:   { cls: 'amber', label: 'Unpaid' },
    paused:   { cls: 'amber', label: 'Paused' },
  };
  const m = map[status] || { cls: 'gray', label: status };
  return <span className={`pill ${m.cls}`}>{m.label}</span>;
};

const BillingInner = () => {
  const { profile } = useShell();
  const { pricing } = usePricing();
  const shopId = profile?.shop_id;
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState(null);   // 'upgrade' | 'portal' | 'downgrade'
  const [error, setError] = useState('');
  const [params, setParams] = useSearchParams();

  const refresh = async () => {
    if (!shopId) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const headers = { 'Authorization': `Bearer ${session?.access_token}` };
      const res = await fetch(`${BACKEND_URL}/api/billing/status/${shopId}`, { headers });
      if (res.ok) setBilling(await res.json());
    } catch (e) {
      console.error('billing/status failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [shopId]);

  // After returning from Stripe Checkout success, hit the status endpoint a few
  // times so the webhook has time to land.
  useEffect(() => {
    if (params.get('billing') !== 'success') return;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < 6 && !cancelled; i++) {
        await new Promise(r => setTimeout(r, 1500));
        await refresh();
      }
      setParams({}, { replace: true });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [params.get('billing')]);

  const plan = (billing?.plan || profile?.shops?.plan || 'standard').toLowerCase();
  const status = billing?.subscription_status || null;
  const isActivePremium = plan === 'premium' && (status === 'active' || status === 'trialing');
  // Interval on the current live subscription (Standard/Premium alike)
  const currentInterval = (billing?.subscription_interval || profile?.shops?.subscription_interval || 'monthly').toLowerCase();
  // Interval the visitor is browsing at right now (drives the two cards).
  // Defaults to whatever the shop is already on so the cards read
  // "current" correctly on first render.
  const [viewInterval, setViewInterval] = useState(currentInterval);
  useEffect(() => { setViewInterval(currentInterval); }, [currentInterval]);

  const isSubscribed = status === 'active' || status === 'trialing';

  const handleUpgrade = async () => {
    setBusyAction('upgrade');
    setError('');
    try {
      const origin = window.location.origin;
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${BACKEND_URL}/api/billing/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          shop_id: shopId,
          plan: 'premium',
          interval: viewInterval,
          success_url: `${origin}/billing?billing=success`,
          cancel_url:  `${origin}/billing?billing=cancelled`,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to start checkout');
      if (data.mode === 'modified') {
        // In-place upgrade — refresh status.
        await refresh();
      } else {
        window.location.href = data.url;
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyAction(null);
    }
  };

  const handlePortal = async () => {
    setBusyAction('portal');
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${BACKEND_URL}/api/billing/portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          shop_id: shopId,
          return_url: `${window.location.origin}/billing`,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to open billing portal');
      window.location.href = data.url;
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyAction(null);
    }
  };

  const handleDowngrade = async () => {
    // Downgrade = open the customer portal so they can swap to Standard or cancel.
    handlePortal();
  };

  const nextCharge = billing?.current_period_end ? fmtDate(billing.current_period_end) : null;
  const subtitle = useMemo(() => {
    const planLabel = plan === 'premium' ? 'Premium' : 'Standard';
    const cycleLabel = currentInterval === 'annual' ? ' · yearly' : '';
    if (!status || status === 'cancelled') return `You're on ${planLabel}${cycleLabel}`;
    if (billing?.cancel_at_period_end) return `You're on ${planLabel}${cycleLabel} · cancels ${nextCharge}`;
    return `You're on ${planLabel}${cycleLabel} · next charge ${nextCharge || '—'}`;
  }, [plan, status, billing, nextCharge, currentInterval]);

  return (
    <main className="main">
      <div className="topbar">
        <div>
          <h2>Billing &amp; plan</h2>
          <div className="sub" data-testid="billing-subtitle">{subtitle}</div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <StatusPill status={status} cancel_at_period_end={billing?.cancel_at_period_end} />
        </div>
      </div>

      <div className="body">
        {error && <div className="modal-error" data-testid="billing-error" style={{ marginBottom: 12 }}>{error}</div>}
        {params.get('billing') === 'success' && (
          <div className="upgrade-nudge" style={{ marginBottom: 14 }} data-testid="billing-success-banner">
            <Check size={16} />
            <div><b>Thanks — your subscription is processing.</b> This page will refresh shortly.</div>
          </div>
        )}
        {params.get('billing') === 'cancelled' && (
          <div className="upgrade-nudge" style={{ marginBottom: 14, background: 'var(--paper)', borderColor: 'var(--line)', color: 'var(--mute)' }}>
            Checkout cancelled — you weren&apos;t charged.
          </div>
        )}

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--mute)' }}>Loading…</div>
        ) : (
          <>
            <div className="segbar" role="tablist" aria-label="Billing cycle" data-testid="billing-interval-toggle" style={{ marginBottom: 14 }}>
              <button
                type="button"
                role="tab"
                aria-selected={viewInterval === 'monthly'}
                className={`seg ${viewInterval === 'monthly' ? 'on' : ''}`}
                onClick={() => setViewInterval('monthly')}
                data-testid="billing-interval-monthly"
              >Monthly</button>
              <button
                type="button"
                role="tab"
                aria-selected={viewInterval === 'annual'}
                className={`seg ${viewInterval === 'annual' ? 'on' : ''}`}
                onClick={() => setViewInterval('annual')}
                data-testid="billing-interval-annual"
              >
                Yearly
                {Number(pricing?.annualDiscountPct || 0) > 0 && (
                  <span className="seg-badge">−{Math.round(pricing.annualDiscountPct)}%</span>
                )}
              </button>
            </div>
            {isSubscribed && viewInterval !== currentInterval && (
              <div className="hint" data-testid="billing-interval-switch-hint" style={{ marginBottom: 10, background: '#fef7e0', border: '1px solid #f6cf65', color: '#7a5b00', padding: '8px 12px', borderRadius: 6, fontSize: 12.5 }}>
                You&apos;re currently billed <b>{currentInterval === 'annual' ? 'yearly' : 'monthly'}</b>. To switch to {viewInterval === 'annual' ? 'yearly' : 'monthly'} billing on your live subscription, open <button type="button" className="link" style={{ background: 'none', border: 0, padding: 0, color: '#7a5b00', textDecoration: 'underline', cursor: 'pointer' }} onClick={handlePortal} data-testid="billing-open-portal-for-switch">Manage subscription</button> and Stripe will pro-rate the change.
              </div>
            )}

            <div className="plans" data-testid="plans">
              {/* STANDARD CARD */}
              <div className={`plan ${plan === 'standard' && (currentInterval === viewInterval) ? 'cur' : ''}`} data-testid="plan-standard">
                <div className="nm">Standard</div>
                {viewInterval === 'annual' ? (() => { const a = annualPrice(pricing, 'standard'); return (
                  <>
                    <div className="pr">£{a.yearly}<small>/yr</small></div>
                    <div style={{ fontSize: 12, color: 'var(--mute)', marginTop: 2 }}>£{a.perMonth}/mo effective{a.saveMonthly > 0 && <> · save £{a.saveMonthly}</>}</div>
                  </>
                );})() : (
                  <div className="pr">£{pricing.standard.price}<small>/mo</small></div>
                )}
                {plan === 'standard' && currentInterval === viewInterval && (
                  <span className="cur-tag" data-testid="standard-current-tag">● Your current plan</span>
                )}
                <ul>
                  {pricing.standard.features.map((f) => (
                    <li key={f}><Check size={15} stroke="#10b981" strokeWidth={3} />{f}</li>
                  ))}
                </ul>
                {plan === 'standard' && currentInterval === viewInterval ? (
                  <button className="btn s" style={{ opacity: .6, cursor: 'default' }} disabled>Current plan</button>
                ) : (
                  <button className="btn s" onClick={handleDowngrade} disabled={busyAction !== null} data-testid="downgrade-btn">
                    {busyAction === 'downgrade' ? '…' : 'Switch to Standard'}
                  </button>
                )}
              </div>

              {/* PREMIUM CARD */}
              <div className={`plan ${isActivePremium && (currentInterval === viewInterval) ? 'cur' : 'up'}`} data-testid="plan-premium">
                {!(isActivePremium && currentInterval === viewInterval) && <span className="badge2">RECOMMENDED</span>}
                <div className="nm" style={{ color: 'var(--acc-ink)' }}>Premium</div>
                {viewInterval === 'annual' ? (() => { const a = annualPrice(pricing, 'premium'); return (
                  <>
                    <div className="pr">£{a.yearly}<small>/yr</small></div>
                    <div style={{ fontSize: 12, color: 'var(--mute)', marginTop: 2 }}>£{a.perMonth}/mo effective{a.saveMonthly > 0 && <> · save £{a.saveMonthly}</>}</div>
                  </>
                );})() : (
                  <div className="pr">£{pricing.premium.price}<small>/mo</small></div>
                )}
                {isActivePremium && currentInterval === viewInterval ? (
                  <span className="cur-tag" data-testid="premium-current-tag">● Your current plan</span>
                ) : (
                  <span className="plus-line">Everything in Standard, plus —</span>
                )}
                <ul>
                  {pricing.premium.features.map((f) => (
                    <li key={f}><Check size={15} stroke="#4f46ff" strokeWidth={3} />{f}</li>
                  ))}
                </ul>
                {isActivePremium && currentInterval === viewInterval ? (
                  <button className="btn s" onClick={handlePortal} disabled={busyAction !== null} data-testid="manage-btn">
                    {busyAction === 'portal' ? '…' : 'Manage subscription'}
                  </button>
                ) : (
                  <button className="btn p" onClick={handleUpgrade} disabled={busyAction !== null || (isSubscribed && viewInterval !== currentInterval)} data-testid="upgrade-btn">
                    {busyAction === 'upgrade' ? 'Loading…' : (viewInterval === 'annual' ? 'Upgrade to Premium yearly →' : 'Upgrade to Premium →')}
                  </button>
                )}
              </div>
            </div>

            {/* Extras panel — only for Premium with at least 1 paid extra barber */}
            {isActivePremium && (billing?.extra_barbers || 0) > 0 && (
              <div className="panel" style={{ marginTop: 14 }} data-testid="extras-panel">
                <div className="ph">
                  <h3>Extra barbers</h3>
                  <span className="pill prem">{billing.extra_barbers} × £{pricing.extraBarberPrice}/mo</span>
                </div>
                <div style={{ padding: '14px 16px', fontSize: 13, color: 'var(--mute)' }}>
                  You have <b>{billing.extra_barbers}</b> paid extra barber
                  slot{billing.extra_barbers === 1 ? '' : 's'} beyond the
                  {' '}{pricing.premium.includedBarbers} included with Premium. Total add-on:
                  <b style={{ color: 'var(--ink)', marginLeft: 4 }}>£{billing.extra_barbers * pricing.extraBarberPrice}/mo</b>.
                  Remove barbers from the <Link to="/barbers" className="link">Barbers</Link> page to reduce this.
                </div>
              </div>
            )}

            {/* Promotion (free trial) — read-only for shop owners */}
            <PromotionSummary shopId={shopId} />

            {/* Payment method panel */}
            <div className="panel" style={{ marginTop: 14 }} data-testid="payment-method-panel">
              <div className="ph">
                <h3>Payment method</h3>
                {billing?.stripe_customer_id && (
                  <span className="link" onClick={handlePortal} data-testid="update-payment-link">Update</span>
                )}
              </div>
              <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
                {billing?.card_last4 ? (
                  <>
                    <span style={{ width: 38, height: 26, background: 'var(--ink)', borderRadius: 5 }} />
                    <span style={{ textTransform: 'capitalize' }}>{billing.card_brand} ending <b className="mono">{billing.card_last4}</b></span>
                    {billing.card_exp && (
                      <span style={{ marginLeft: 'auto', color: 'var(--mute)', fontSize: 12.5 }}>Expires {billing.card_exp}</span>
                    )}
                  </>
                ) : (
                  <span style={{ color: 'var(--mute)' }}>No card on file yet — added at upgrade.</span>
                )}
              </div>
            </div>

            {/* SMS bundles — top-up credits for both Standard and Premium.
                Requires a Stripe customer, which is created by the plan
                subscribe flow above. Skip rendering until one exists.
                `id="sms-bundles"` lets the Dashboard's "Top up SMS credits"
                link deep-link straight to the panel. */}
            {billing?.stripe_customer_id && (
              <div id="sms-bundles" style={{ marginTop: 14 }}>
                <SmsBundlesPanel shopId={shopId} />
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
};

// Read-only free-trial promotion summary for shop owners. Renders both
// per-shop promotions AND the global signup trial (when applicable to
// this shop). Silently hides when nothing applies.
const PromotionSummary = ({ shopId }) => {
  const [promo, setPromo] = useState(null);
  const [resolved, setResolved] = useState(null);

  useEffect(() => {
    if (!shopId) return;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const headers = { 'Authorization': `Bearer ${session?.access_token}` };
        const [pRes, rRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/promotions/${shopId}`, { headers }),
          fetch(`${BACKEND_URL}/api/promotions/${shopId}/resolved`, { headers }),
        ]);
        if (pRes.ok) setPromo(await pRes.json());
        if (rRes.ok) setResolved(await rRes.json());
      } catch (e) { console.debug('[billing] promotions fetch failed:', e); }
    })();
  }, [shopId]);

  if (!resolved) return null;
  const hasPerShop = !!(promo && promo.trial_mode);
  const showGlobal = resolved.source === 'global_signup' && resolved.days > 0;
  if (!hasPerShop && !showGlobal) return null;

  const fmt = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="panel" style={{ marginTop: 14 }} data-testid="promotion-summary">
      <div className="ph">
        <h3>Promotion</h3>
        <span className="pill prem">Free trial</span>
      </div>
      <div style={{ padding: '14px 16px', fontSize: 13, color: 'var(--mute)' }}>
        {hasPerShop && promo.trial_mode === 'days' && (
          <>Your account has a <b style={{ color: 'var(--ink)' }}>{promo.trial_days}-day free trial</b> ready to apply when you start your subscription. Card details are required at checkout — no charge will be taken during the trial.</>
        )}
        {hasPerShop && promo.trial_mode === 'range' && (
          <>Your free trial runs from <b style={{ color: 'var(--ink)' }}>{fmt(promo.trial_starts_at)}</b> to <b style={{ color: 'var(--ink)' }}>{fmt(promo.trial_ends_at)}</b>{promo.effective_days ? <> — that&apos;s <b style={{ color: 'var(--ink)' }}>{promo.effective_days} free day{promo.effective_days === 1 ? '' : 's'}</b> when you subscribe today</> : null}. Card details are required at checkout — no charge will be taken during the trial.</>
        )}
        {!hasPerShop && showGlobal && (
          <>New shops on Instacut get a <b style={{ color: 'var(--ink)' }}>{resolved.days}-day free trial</b> on their first subscription. Card details are required at checkout — no charge will be taken during the trial.</>
        )}
      </div>
    </div>
  );
};


const Billing = () => (
  <AppShell>
    <BillingInner />
  </AppShell>
);

export default Billing;
